<template>
  <b-row v-if="company">
    <b-col cols="12" md="8" offset-md="2">
      <b-card class="shadow mx-md-10 px-1 px-md-10 py-2" body-class="pt-0">
        <img src="@src/assets/integrations/slack-logo.svg" width="200px"/>
        <template v-if="!slack || loading">
          <no-ssr><loading-spinner class="d-flex justify-content-center my-5"/></no-ssr>
        </template>
        <template v-else-if="slack && !slack.company">
          <p>Slack has not been configured for this site.</p>
        </template>
        <b-row v-else>
          <b-col md="6">
            <p>You can optionally connect your Slack account to receive notifications as direct messages:</p>
            <ul>
              <li>When candidates apply with your unique share links.</li>
              <li>As candidates move through the hiring process.</li>
            </ul>
            <p>You will receive email notifications regardless of whether you link your Slack account or not.</p>
          </b-col>
          <b-col md="6">
            <template v-if="loading">
              <no-ssr><loading-spinner class="d-flex justify-content-center my-5"/></no-ssr>
            </template>
            <template v-else-if="slack && slack.user && slack.user.team_id">
              <table class="table b-table b-table-stacked">
                <tbody>
                  <tr>
                    <td data-label="Status"><span class="text-success">Connected!</span></td>
                    <td data-label="Team">{{ slack.company.team_name }}</td>
                    <td data-label="User">
                      <div class="d-flex flex-row justify-content-start align-items-center">
                        <img class="rounded mr-1" :src="slack.user.user_avatar" height="25px" width="25px"/>
                        {{ slack.user.user_name }}
                      </div>
                    </td>
                    <td data-label="">
                      <b-btn class="d-flex flex-row justify-content-start align-items-center" @click="disconnectSlack" variant="slack">
                        <img src="@src/assets/integrations/slack-mark.svg" height="30px"/>
                        <strong>Disconnect</strong>
                      </b-btn>
                    </td>
                  </tr>
                </tbody>
              </table>
            </template>
            <b-btn v-else class="d-flex flex-row justify-content-start align-items-center" :href="redirectToSlack" variant="slack">
              <img src="@src/assets/integrations/slack-mark.svg" height="30px"/>
              <strong>Connect your Slack account</strong>
            </b-btn>
          </b-col>
        </b-row>

        <b-row>
          <b-col class="d-flex justify-content-between align-items-center px-3">
            <a class="text-secondary" href="#" @click.prevent="() => $emit('goNext')">Skip this step</a>
            <b-button @click.prevent="() => $emit('goNext')" variant="primary">Next</b-button>
          </b-col>
        </b-row>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import axios from 'axios';
import base64 from 'base-64';
import gql from 'graphql-tag';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import { mapRoot } from '@src/mixins/root';

const GetSlackStateQuery = gql`
  query GetSlackState {
    company {
      id
      state {
        slack
      }
    }
    user {
      id
      state {
        slack
      }
    }
  }
`;
const UpdateSlackStateMutation = gql`
  mutation SetUserSlackState($userID: ID!, $user: KeyValueHash) {
    user: updateUser(update: { state: { slack: $user } }, where: { id: $userID }) {
      id
      state {
        slack
      }
    }
  }
`;

export default {
  name: 'OnboardingSlack',
  components: {
    LoadingSpinner,
  },
  data: () => ({
    loading: true,
  }),
  apollo: {
    slack: {
      query: GetSlackStateQuery,
      update: ({ company, user }) => ({
        company: ((company || {}).state || {}).slack || null,
        user: ((user || {}).state || {}).slack || null,
      }),
      async result({ loading, data }) {
        if (!loading && data && data.company) {
          const { hash } = this.$route;
          if (`${hash}`.length > 1) {
            const parsed = JSON.parse(base64.decode(hash.substr(1)));
            await this.parseResponse(parsed);
            this.$router.replace({ hash: '' });
          }
          this.loading = false;
        }
      }
    },
  },
  metaInfo: {
    title: 'Connect Slack',
  },
  computed: {
    ...mapRoot([ 'company', 'user', 'servicesOrigin' ]),
    redirectToSlack() {
      if (this.slack && this.slack.company) {
        const { team_id: teamID } = this.slack.company;
        const returnUri = `${this.$root.origin}${this.$route.path}`;
        const userScope = 'identity.basic,identity.avatar';
        return `${this.servicesOrigin}/integrations/slack/auth?team=${teamID}&return_uri=${returnUri}&user_scope=${userScope}`;
      } else {
        return null;
      }
    },
  },
  methods: {
    async parseResponse(res) {
      try {
        this.$log.debug(res);

        this.$assert(res && res.ok, new Error(res.error));
        this.$assert(res.authed_user && res.authed_user.access_token, new Error('Missing user access token'));

        const { data: { user } } = await axios.get('https://slack.com/api/users.identity', {
          params: { token: res.authed_user.access_token },
          validateStatus: status => status === 200,
        });

        this.$log.debug({ user });

        const avatar = user.image_1024 || user.image_512 || user.image_256 || user.image_192 || user.image_72;

        const userState = {
          app_id: res.app_id,
          team_id: res.team.id,
          user_id: res.authed_user.id,
          user_name: user.name,
          user_avatar: avatar,
          user_scope: res.authed_user.scope,
          user_token: res.authed_user.access_token,
        };

        const { id: userID } = this.user;
        await this.$apollo.mutate({
          mutation: UpdateSlackStateMutation,
          variables: { userID, user: userState },
        });
        this.$emit('goNext');
      } catch (err) {
        this.$notifyErr(err, { prepend: 'Failed to add to Slack' });
      }
    },
    async disconnectSlack() {
      try {
        this.loading = true;
        const { id: userID } = this.user;
        await this.$apollo.mutate({
          mutation: UpdateSlackStateMutation,
          variables: { userID, user: null },
        });
      } catch (err) {
        this.$notifyErr(err, { prepend: 'Failed to remove from Slack' });
        this.loading = false;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/onboarding/Slack.vue